import React from 'react';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import { IFullwidthHero20Fields } from '../../../types/contentful';

import Link from 'next/link';
import RichText from '../RichText';

type DesktopPolicyDisclaimerFields = Pick<
  IFullwidthHero20Fields,
  'desktopImageDisclaimer'
>;

interface DesktopPolicyDisclaimerProps extends DesktopPolicyDisclaimerFields {
  alignContentToLeft?: boolean;
  noMargin?: boolean;
  colorHex?: string;
}

export default function DesktopImageDisclaimer({
  desktopImageDisclaimer,
  noMargin = false,
  colorHex
}: DesktopPolicyDisclaimerProps) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p
          className="max-w-[272px] tablet:text-center desktop:min-w-0 desktop:max-w-[496px]"
          style={colorHex ? { color: colorHex } : { color: '#757575' }}
        >
          {children}
        </p>
      );
    },
    [BLOCKS.HEADING_6]: (_node: Block | Inline, children) => (
      <p className="mb-4 font-medium">{children}</p>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const href = node.data.uri as string;
      const hoverClass =
        colorHex && colorHex !== '#1D252C'
          ? 'hover:text-[#3BBFAD]'
          : 'hover:text-[#035C67]';
      return (
        <Link
          href={href}
          className={`text-inherit underline underline-offset-2 transition-colors duration-200 ease-in-out ${hoverClass}`}
          style={colorHex ? { color: colorHex } : undefined}
        >
          {children}
        </Link>
      );
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      type InlineEntryTarget = {
        data?: {
          target?: {
            sys?: { contentType?: { sys?: { id?: string } } };
            fields?: {
              url?: string;
              title?: string;
              openInNewTab?: boolean;
            };
          };
        };
      };
      const { data } = node as InlineEntryTarget;
      const target = data?.target;
      const sys = target?.sys;
      const fields = target?.fields ?? {};
      const { url, title, openInNewTab } = fields as {
        url?: string;
        title?: string;
        openInNewTab?: boolean;
      };

      if (sys?.contentType?.sys?.id === 'link' && url) {
        const hoverClass =
          colorHex && colorHex !== '#1D252C'
            ? 'hover:text-[#3BBFAD]'
            : 'hover:text-[#035C67]';
        return (
          <a
            href={url}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            className={`text-inherit underline underline-offset-2 transition-colors duration-200 ease-in-out ${hoverClass}`}
            style={colorHex ? { color: colorHex } : undefined}
          >
            {title}
          </a>
        );
      }
      return null;
    }
  };

  return (
    <div className="col-span-12 mx-auto tablet:mx-0">
      <div
        className={`flex flex-row items-center justify-between ${
          noMargin ? 'm-0' : 'm-auto tablet:m-0'
        }`}
      >
        <div className="flex flex-col">
          <RichText
            data={desktopImageDisclaimer}
            overrideOptions={RICH_TEXT_OVERRIDE}
          />
        </div>
      </div>
    </div>
  );
}
